import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export interface TopItemReport {
  item_id: number;
  item_sku: string;
  item_name: string;
  category: string;
  qty_sold: number;
  total_revenue: number;
  total_profit: number;
}

export interface TopCustomerReport {
  customer_id?: number;
  customer_name: string;
  customer_phone?: string;
  total_transactions: number;
  total_spent: number;
  total_items_purchased: number;
}

export interface PaymentBreakdown {
  cash: number;
  transfer: number;
  qris: number;
  edc: number;
}

export interface DailySalesReport {
  date: string;
  branch_id?: number;
  branch_name?: string;
  total_transactions: number;
  total_items_sold: number;
  gross_sales: number;
  total_discount: number;
  net_sales: number;
  total_cost: number;
  gross_profit: number;
  profit_margin: number;
  payment_breakdown: PaymentBreakdown;
  top_items: TopItemReport[];
  void_count: number;
  void_amount: number;
}

export interface MonthlySalesReport {
  year: number;
  month: number;
  month_name: string;
  branch_id?: number;
  branch_name?: string;
  total_transactions: number;
  total_items_sold: number;
  avg_transactions_per_day: number;
  gross_sales: number;
  total_discount: number;
  net_sales: number;
  total_cost: number;
  gross_profit: number;
  profit_margin: number;
  avg_daily_sales: number;
  best_day_date?: string;
  best_day_sales: number;
  worst_day_date?: string;
  worst_day_sales: number;
  payment_breakdown: PaymentBreakdown;
  top_items: TopItemReport[];
  top_customers: TopCustomerReport[];
  previous_month_sales?: number;
  growth_amount?: number;
  growth_percentage?: number;
}

const API_URL = `${API_BASE_URL}/reports`;

export const getDailyReport = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<DailySalesReport> => {
  const response = await api.get<any>(
    `${API_URL}/daily`,
    { params }
  );
  
  // Convert string values to numbers
  const data = response.data;
  return {
    ...data,
    gross_sales: parseFloat(data.gross_sales) || 0,
    total_discount: parseFloat(data.total_discount) || 0,
    net_sales: parseFloat(data.net_sales) || 0,
    total_cost: parseFloat(data.total_cost) || 0,
    gross_profit: parseFloat(data.gross_profit) || 0,
    profit_margin: parseFloat(data.profit_margin) || 0,
    void_amount: parseFloat(data.void_amount) || 0,
    payment_breakdown: {
      cash: parseFloat(data.payment_breakdown?.cash) || 0,
      transfer: parseFloat(data.payment_breakdown?.transfer) || 0,
      qris: parseFloat(data.payment_breakdown?.qris) || 0,
      edc: parseFloat(data.payment_breakdown?.edc) || 0,
    },
    top_items: data.top_items?.map((item: any) => ({
      ...item,
      total_revenue: parseFloat(item.total_revenue) || 0,
      total_profit: parseFloat(item.total_profit) || 0,
    })) || [],
  };
};

export const getMonthlyReport = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<MonthlySalesReport> => {
  const response = await api.get<any>(
    `${API_URL}/monthly`,
    { params }
  );
  
  // Convert string values to numbers
  const data = response.data;
  return {
    ...data,
    avg_transactions_per_day: parseFloat(data.avg_transactions_per_day) || 0,
    gross_sales: parseFloat(data.gross_sales) || 0,
    total_discount: parseFloat(data.total_discount) || 0,
    net_sales: parseFloat(data.net_sales) || 0,
    total_cost: parseFloat(data.total_cost) || 0,
    gross_profit: parseFloat(data.gross_profit) || 0,
    profit_margin: parseFloat(data.profit_margin) || 0,
    avg_daily_sales: parseFloat(data.avg_daily_sales) || 0,
    best_day_sales: parseFloat(data.best_day_sales) || 0,
    worst_day_sales: parseFloat(data.worst_day_sales) || 0,
    payment_breakdown: {
      cash: parseFloat(data.payment_breakdown?.cash) || 0,
      transfer: parseFloat(data.payment_breakdown?.transfer) || 0,
      qris: parseFloat(data.payment_breakdown?.qris) || 0,
      edc: parseFloat(data.payment_breakdown?.edc) || 0,
    },
    top_items: data.top_items?.map((item: any) => ({
      ...item,
      total_revenue: parseFloat(item.total_revenue) || 0,
      total_profit: parseFloat(item.total_profit) || 0,
    })) || [],
    top_customers: data.top_customers?.map((customer: any) => ({
      ...customer,
      total_spent: parseFloat(customer.total_spent) || 0,
    })) || [],
    previous_month_sales: data.previous_month_sales ? parseFloat(data.previous_month_sales) : undefined,
    growth_amount: data.growth_amount ? parseFloat(data.growth_amount) : undefined,
    growth_percentage: data.growth_percentage ? parseFloat(data.growth_percentage) : undefined,
  };
};

export const exportDailyReportPDF = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/daily/export/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportDailyReportExcel = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/daily/export/excel`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportMonthlyReportPDF = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/monthly/export/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportMonthlyReportExcel = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/monthly/export/excel`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

// Aliases for backward compatibility
export const getSalesReport = getDailyReport;
export const getStockReport = async (params?: { branch_id?: number }) => {
  // TODO: Implement stock report endpoint when backend is ready
  return [];
};
